/* Hallmark · macrostructure: Workbench · tone: scholarly-utilitarian · anchor hue: heritage green
 * pre-emit critique: P5 H5 E4 S5 R4 V4
 */
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router';

export interface CurriculumPathItem {
  key: string;
  label: string;
  value: string;
  href?: string;
  current?: boolean;
}

interface CurriculumPathProps {
  ariaLabel: string;
  eyebrow: string;
  items: CurriculumPathItem[];
}

export function CurriculumPath({ ariaLabel, eyebrow, items }: CurriculumPathProps) {
  return (
    <nav aria-label={ariaLabel} className="rounded-3xl bg-surface-container-low p-4 sm:p-5">
      <p className="mb-3 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
        {eyebrow}
      </p>
      <ol className="m-0 flex list-none items-stretch gap-2 overflow-x-auto p-0 pb-1">
        {items.map((item, index) => {
          const content = (
            <>
              <span
                className={`block text-[9px] font-label font-bold uppercase tracking-wider ${
                  item.current ? 'text-on-primary/75' : 'text-on-surface-variant'
                }`}
              >
                {item.label}
              </span>
              <span className="mt-1 block max-w-44 truncate text-xs font-bold leading-snug">
                {item.value}
              </span>
            </>
          );

          return (
            <li key={item.key} className="flex min-w-0 items-center gap-2">
              {item.href ? (
                <Link
                  to={item.href}
                  aria-current={item.current ? 'step' : undefined}
                  className={`min-w-32 rounded-xl px-3 py-2.5 no-underline transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 ${
                    item.current
                      ? 'bg-primary text-on-primary shadow-level1'
                      : 'bg-surface-container-lowest text-on-surface hover:bg-surface-container-high'
                  }`}
                >
                  {content}
                </Link>
              ) : (
                <div
                  aria-current={item.current ? 'step' : undefined}
                  className={`min-w-32 rounded-xl px-3 py-2.5 ${
                    item.current
                      ? 'bg-primary text-on-primary shadow-level1'
                      : 'bg-surface-container-lowest text-on-surface'
                  }`}
                >
                  {content}
                </div>
              )}
              {index < items.length - 1 && (
                <ChevronRight
                  size={14}
                  className="shrink-0 text-on-surface-variant/50"
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
